/* Gift quantities are derived from merchant gift_lines. Checkout enforcement
 * against forged requests still requires a Shopify Cart Validation Function. */
(() => {
  'use strict';
  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const rules = new Map();
  let queue = Promise.resolve();
  const readRule = (node) => JSON.parse(node.textContent);
  document.querySelectorAll('[data-xr-gift-rules]').forEach(node => {
    rules.set(node.dataset.handle, Promise.resolve(readRule(node)));
  });
  async function json(path, body) {
    const response = await originalFetch(root + path, body ? {
      method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(body)
    } : undefined);
    if (!response.ok) throw new Error('Unable to update complimentary gifts. Please retry your cart.');
    return response.json();
  }
  function getRule(handle) {
    if (!rules.has(handle)) rules.set(handle, (async () => {
      const response = await originalFetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=xr-gift-data');
      if (!response.ok) throw new Error('Gift rules could not be loaded. Please retry.');
      const node = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-xr-gift-rules]');
      if (!node) throw new Error('Gift rules are unavailable. Please refresh this page.');
      return readRule(node);
    })().catch(error => { rules.delete(handle); throw error; }));
    return rules.get(handle);
  }
  async function reconcile() {
    let cart = await json('cart.js');
    const configs = await Promise.all(cart.items.map(item => getRule(item.handle)));
    const desired = new Map();
    cart.items.forEach((item, index) => {
      if (item.properties?._gift_item || configs[index].giftOnly) return;
      for (const gift of configs[index].gifts) {
        const parent = String(item.variant_id);
        const key = parent + ':' + gift.id;
        const previous = desired.get(key);
        desired.set(key, {id: gift.id, parent, quantity: (previous?.quantity || 0) + item.quantity * gift.quantity});
      }
    });
    const updates = {};
    cart.items.forEach((item, index) => {
      if (!item.properties?._gift_item && !configs[index].giftOnly) return;
      const key = String(item.properties?._gift_parent || '').replace('gid://shopify/ProductVariant/', '') + ':' + item.variant_id;
      const target = item.properties?._gift_item ? desired.get(key) : null;
      const quantity = target?.quantity || 0;
      if (quantity !== item.quantity) updates[item.key] = quantity;
      if (target) desired.delete(key);
    });
    if (Object.keys(updates).length) await json('cart/update.js', {updates});
    const items = [...desired.values()].map(gift => ({id:gift.id, quantity:gift.quantity,
      properties:{_gift_parent:gift.parent,_gift_item:'true'}}));
    if (items.length) await json('cart/add.js', {items});
    return json('cart.js');
  }
  window.fetch = function(input, init) {
    const url = new URL(input instanceof URL || typeof input === 'string' ? input : input.url, location.href);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (url.origin !== location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      return originalFetch(input, init);
    }
    const operation = async () => {
      const response = await originalFetch(input, init);
      if (!response.ok) return response;
      const payload = await response.clone().json();
      const cart = await reconcile();
      let result = /\/add/.test(url.pathname) ? payload : {...payload, ...cart};
      if (payload.sections) {
        const sectionUrl = new URL(location.href);
        sectionUrl.searchParams.set('sections', Object.keys(payload.sections).join(','));
        const refreshed = await originalFetch(sectionUrl);
        if (!refreshed.ok) throw new Error('Cart updated. Refresh to see your gifts.');
        result.sections = await refreshed.json();
      }
      document.dispatchEvent(new CustomEvent('xr:gifts-synced', {detail:cart}));
      return new Response(JSON.stringify(result), {status:response.status, headers:{'Content-Type':'application/json'}});
    };
    const pending = queue.then(operation);
    queue = pending.catch(() => {});
    return pending;
  };
  // Native cart checkout forms do not necessarily go through fetch.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (event.submitter?.name !== 'checkout' || form.dataset.xrGiftChecked === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter;
    button.disabled = true;
    try {
      await queue;
      await reconcile();
      form.dataset.xrGiftChecked = 'true';
      button.disabled = false;
      form.requestSubmit(button);
    } catch (error) {
      button.disabled = false;
      window.alert(error.message);
    }
  }, true);
})();
